import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { Helmet } from "react-helmet";
import Breadcrumb from "../../common/breadcrumb";
import backend from "../../../api";
import { useDispatch, useSelector } from "react-redux";
import { setDefaultPayment } from "../../../redux-store/paymentMethod/action";
import useDebounce from "../Dashboard/components/AdWorkShop/hooks/useDebounce";
import {
  Tabs,
  Tab,
  Switch,
  Button,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import CustomPagination from "../../common/CustomPagination";
import CountWidget from "../../common/cards/CountWidget";

const AccountList = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(history?.location?.search);
  const accountType = searchParams.get("accountType");
  const { user } = useSelector((state) => state.auth);
  const [accounts, setAccounts] = useState([]);
  const [searchQuery, SetSearchQuery] = useState("");
  const [totalPages, SetTotalPages] = useState(1);
  const [filter, setFilter] = useState({
    userId: user?.user?._id,
    page: 1,
    limit: 10,
    type: accountType || "e-money",
    isVerified: true,
  });
  const [accountSummary, setAccountSummary] = useState({
    completeEAccounts: 0,
    inProgressEAccounts: 0,
    completeBankAccounts: 0,
    inProgressBankAccounts: 0,
  });

  const search = useDebounce(searchQuery, 500);
  const params = useMemo(() => ({ ...filter, search }), [filter, search]);

  const bankData = [
    {
      title: "Completed",
      value: accountSummary?.completeBankAccounts,
    },
    {
      title: "In-Progress",
      value: accountSummary?.inProgressBankAccounts,
    },
  ];

  const eMoneyData = [
    {
      title: "Completed",
      value: accountSummary?.completeEAccounts,
    },
    {
      title: "In-Progress",
      value: accountSummary?.inProgressEAccounts,
    },
  ];

  const handleFilter = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleTab = (event, newValue) => {
    handleFilter("type", newValue);
    SetSearchQuery("");
  };

  const handleType = (e) => {
    handleFilter("type", e.target.value);
    SetSearchQuery("");
  };

  const handleStatus = (e) => {
    handleFilter("isVerified", e.target.value);
  };

  const handleSearch = (e) => {
    SetSearchQuery(e.target.value);
  };

  const handlePageChange = (selectedPage) => {
    handleFilter("page", selectedPage.selected + 1);
  };

  const hasBankInfo = (account) => {
    return account?.additionalInfo?.bank_name && account?.additionalInfo?.last4;
  };

  const handleNavigation = (account) => {
    let redirect = "#";

    if (account.type === "bank-account") {
      redirect = `/complete-onboard/${account?.type}/${account?.accountId}/${account?._id}`;
    } else if (account.type === "e-money") {
      redirect = `/view-account/${account.type}/${account?.accountId}/${account?._id}`;
    }

    history.push(redirect);
  };

  const getAccounts = async (params) => {
    try {
      const response = await backend.get("/accounts", { params });
      const { accounts, totalPages, totalDocs } = response?.data;
      setAccounts(accounts);
      SetTotalPages(totalPages);
    } catch (error) {
      console.error("error: ", error);
      toast.error(
        error?.response
          ? error?.response?.data?.message
          : "Failed to Fetch account"
      );
    }
  };

  const getAccountsSummary = async (userId) => {
    try {
      const response = await backend.get("/accounts/status-summary", {
        params: { userId },
      });
      const { data } = response?.data;
      setAccountSummary(data);
    } catch (error) {
      console.error("error: ", error);
      toast.error(
        error?.response
          ? error?.response?.data?.message
          : "Failed to Fetch account Summary"
      );
    }
  };

  const makeDefault = async (id, currentValue, accountId) => {
    try {
      if (currentValue) {
        return toast.error("Atleast one Default account required !");
      }

      const newDefaultValue = !currentValue;
      const response = await backend.put(`/accounts/make-default/${id}`, {
        isDefault: newDefaultValue,
      });

      if (response.status === 200) {
        dispatch(setDefaultPayment(accountId));
        toast.success("Account updated successfully");
        getAccounts(filter);
      }
    } catch (error) {
      console.error("error: ", error);
      toast.error(
        error?.response
          ? error?.response?.data?.message
          : "Failed to delete account"
      );
    }
  };

  const handleDelete = async (id, type) => {
    try {
      const endPoint =
        type === "bank-account"
          ? `/connected-account/delete/${id}`
          : `/accounts/delete/${id}`;
      const response = await backend.delete(endPoint);
      toast.success(response?.data?.message);
      setAccounts((prevAccounts) =>
        prevAccounts.filter((account) => account._id !== id)
      );
      // getAccounts();
    } catch (error) {
      console.error("error: ", error);
      toast.error(
        error?.response
          ? error?.response?.data?.message
          : "Failed to delete account"
      );
    }
  };

  useEffect(() => {
    getAccounts(params);
  }, [params]);

  useEffect(() => {
    getAccountsSummary(user?.user?._id);
  }, []);

  return (
    <div>
      <Helmet>
        <title>eksplode! | Account List</title>
      </Helmet>
      <Breadcrumb title={"Account List"} />
      <section className="section-b-space">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="box-head">
                <div className="d-flex justify-content-between">
                  {/* <Tabs value={filter?.type} onChange={handleTab}>
                    <Tab value="e-money" label="E-money" />
                    <Tab value="bank-account" label="Bank" />
                  </Tabs> */}
                  <h3 className="m-0 p-0">
                    {filter?.type === "e-money"
                      ? "E-money Accounts"
                      : "Bank Accounts"}
                  </h3>
                  <Link
                    to={`/add-account/${filter?.type}`}
                    className="btn btn-solid"
                  >
                    Add New
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-4 mb-3">
              <Card>
                <CardHeader title="E-Money" />
                <CardContent>
                  <div className="row">
                    {eMoneyData?.map((el, index) => {
                      return (
                        <div className="col-12 col-sm-6 mb-3" key={index}>
                          <CountWidget data={el} />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="col-md-4 mb-3">
              <Card>
                <CardHeader title="Bank-Account" />
                <CardContent>
                  <div className="row">
                    {bankData?.map((el, index) => {
                      return (
                        <div className="col-12 col-sm-6 mb-3" key={index}>
                          <CountWidget data={el} />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-2 mb-3">
              <div className="d-flex justify-content-start">
                <input
                  type="radio"
                  className="form-control w-auto mr-2"
                  id="e-money"
                  name="e-money"
                  value="e-money"
                  checked={filter?.type === "e-money"}
                  onChange={handleType}
                />
                <label htmlFor="e-money" className="mb-0">
                  E-Money
                </label>
              </div>
              <div className="d-flex justify-content-start">
                <input
                  type="radio"
                  className="form-control w-auto mr-2"
                  id="bank-account"
                  name="bank-account"
                  value="bank-account"
                  checked={filter?.type === "bank-account"}
                  onChange={handleType}
                />
                <label htmlFor="bank-account" className="mb-0">
                  Bank-Account
                </label>
              </div>
            </div>

            <div className="col-8 mb-3">
              <input
                type="text"
                className="form-control mb-0"
                placeholder="Search Accounts"
                onChange={handleSearch}
                value={searchQuery}
              />
            </div>

            <div className="col-2 mb-3">
              <div className="d-flex align-items-center justify-content-end">
                <label className="mb-0 mr-2">Status:</label>
                <select
                  value={filter?.isVerified}
                  className="form-control mb-0"
                  onChange={handleStatus}
                >
                  <option selected value={true}>
                    Completed
                  </option>
                  <option selected value={false}>
                    In Progress
                  </option>
                </select>
              </div>
            </div>

            <div className="col-12">
              <table className="table cart-table table-responsive-xs">
                <thead>
                  <tr className="table-head">
                    <th scope="col">Account No</th>
                    <th scope="col">Type</th>
                    <th scope="col">Default</th>
                    <th scope="col">Status</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.length > 0 ? (
                    accounts.map((account) => (
                      <tr key={account._id}>
                        <td>
                          {hasBankInfo(account) ? (
                            <div className="d-flex flex-column">
                              <span className="mb-1">
                                {account?.additionalInfo?.bank_name}
                              </span>
                              <span>
                                {account?.additionalInfo?.routing_number},{" "}
                                {`****${account?.additionalInfo?.last4}`}
                              </span>
                            </div>
                          ) : (
                            "No Attached Bank info Found"
                          )}
                        </td>
                        <td>{account.type}</td>
                        <td>
                          <Switch
                            size="small"
                            checked={account.default}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "green !important",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: "green !important",
                              },
                            }}
                            onChange={() =>
                              makeDefault(
                                account._id,
                                account.default,
                                account.accountId
                              )
                            }
                          />
                        </td>
                        <td>
                          {account.isVerified ? (
                            "Completed"
                          ) : (
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => handleNavigation(account)}
                            >
                              <small>In Progress, Click to Continue</small>
                            </Button>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-baseline">
                            <Link
                              to={`/view-account/${account.type}/${account?.accountId}/${account?._id}`}
                              className="mr-2"
                            >
                              <i className="fa fa-eye"></i>
                            </Link>
                            <Link
                              to="#"
                              onClick={() =>
                                handleDelete(account?._id, account?.type)
                              }
                            >
                              <i className="fa fa-trash text-danger"></i>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>
                        No Record Found{" "}
                        <Link to={`/add-account/${filter?.type}`}>
                          <small>Add New!</small>
                        </Link>
                      </td>
                    </tr>
                  )}
                </tbody>
                {totalPages > 1 && (
                  <tfoot>
                    <tr>
                      <td colSpan={5}>
                        <div className="d-flex justify-content-center w-100">
                          <CustomPagination
                            handlePageChange={handlePageChange}
                            totalPages={totalPages}
                            currentPage={filter?.page}
                          />
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      </section>
      <ToastContainer />
    </div>
  );
};

export default AccountList;
